// NOKE 專櫃 櫃位 (storage-slot) map + shelf-edge label printing.
//
// Default stock addresses are A1–A10 and B1–B10. Staff may also enter custom
// addresses (C1, A11, A3-2) when the physical cabinet layout changes.
//
// Why plain shelf codes and not the receiving page's auto-assigned 貨架格 pool:
// those bins hold ONE piece belonging to ONE customer order, packed ~8 per bin
// by an allocator. Counter stock is per-VARIANT, sits for a whole 檔期, and the
// human decides where it fits (a coat needs a tall shelf, a 滾珠香水 does not).
// So the system records a location the staffer chose; it never picks one.
//
// The API stores this as a free VARCHAR(16), so re-labelling the cabinet or
// subdividing a shelf ('A3-2') needs no migration.

import JsBarcode from 'jsbarcode';

// ---- Shelf map (change here only) ------------------------------------------
export const SLOT_COLUMNS = ['A', 'B'] as const;
export const SHELVES_PER_COLUMN = 10;

/** Suggested 櫃位, top-to-bottom per column: A1…A10, B1…B10. */
export const NOKE_SLOTS: string[] = SLOT_COLUMNS.flatMap((col) =>
  Array.from({ length: SHELVES_PER_COLUMN }, (_, i) => `${col}${i + 1}`)
);

/** Same normalization as the backend's normalizeSlot: trim, strip spaces, uppercase. */
export function normalizeSlot(v: string | null | undefined): string | null {
  if (v == null) return null;
  const t = String(v).trim().replace(/\s+/g, '').toUpperCase();
  return t === '' ? null : t.slice(0, 16);
}

/**
 * Sort key for a slot code. Known shelves sort in physical walking order;
 * anything unrecognised (a hand-typed or legacy code) sorts after them rather
 * than being hidden, and null sorts last.
 */
export function slotSortKey(slot: string | null | undefined): [number, string] {
  const n = normalizeSlot(slot);
  if (!n) return [2, ''];
  const idx = NOKE_SLOTS.indexOf(n);
  return idx >= 0 ? [0, String(idx).padStart(3, '0')] : [1, n];
}

// ---- Slot barcodes ------------------------------------------------------------
// Each shelf label carries a CODE128 'SLOT-{slot}' under the big text, so the 盤點
// screen can pick (or switch) its 櫃位 from the same scanner that reads hang tags.
// The prefix keeps a slot code from ever resolving as a product barcode.
export const SLOT_BARCODE_PREFIX = 'SLOT-';

/** 'SLOT-a1' (any case) -> 'A1'; anything else -> null. */
export function parseSlotBarcode(code: string | null | undefined): string | null {
  const m = String(code || '').trim().match(/^SLOT-(.+)$/i);
  return m ? normalizeSlot(m[1]) : null;
}

function slotBarcodeSvg(slot: string): string {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  try {
    JsBarcode(svg, `${SLOT_BARCODE_PREFIX}${slot}`, {
      format: 'CODE128', displayValue: false, height: 40, width: 1.8, margin: 0,
    });
  } catch {
    return '';
  }
  return new XMLSerializer().serializeToString(svg);
}

// ---- Shelf-edge labels ------------------------------------------------------
// A4 sheets of large slot codes + a SLOT-{slot} barcode (2 columns x 4 rows = 8 per sheet) to cut out
// and tape to the front edge of each
// shelf. The physical label is the other half of the system: a code in the
// database that isn't readable on the cabinet is a code nobody trusts.
function buildShelfLabelsHtml(slots: string[]): string {
  const cells = slots
    .map(
      (slot) => `
    <div class="cell">
      <div class="code">${slot}</div>
      <div class="barcode">${slotBarcodeSvg(slot)}</div>
      <div class="sub">NOKE 專櫃 櫃位 · 盤點掃這裡</div>
    </div>`
    )
    .join('');

  return `<!DOCTYPE html>
<html lang="zh-Hant">
<head>
<meta charset="utf-8">
<title>NOKE 櫃位標籤</title>
<style>
  @page { size: A4 portrait; margin: 10mm; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: "Noto Sans TC", "Microsoft JhengHei", sans-serif;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6mm;
  }
  .cell {
    height: 62mm;
    border: 1.5mm solid #111;
    border-radius: 3mm;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    page-break-inside: avoid;
  }
  /* Readable from a step back with an armful of stock. */
  .code { font-size: 30mm; font-weight: 800; line-height: 1; letter-spacing: 2mm; }
  /* Scanner target for the 盤點 screen; small enough to leave the code readable. */
  .barcode svg { display: block; width: 60mm; height: 12mm; margin-top: 2mm; }
  .sub  { font-size: 4mm; letter-spacing: 1mm; color: #444; margin-top: 2mm; }
</style>
</head>
<body onload="window.print()">
${cells}
</body>
</html>`;
}

/** Open a print popup with one large label per 櫃位, 8 to an A4 sheet. */
export function printShelfLabels(slots: string[] = NOKE_SLOTS): void {
  if (slots.length === 0) return;
  const win = window.open('', '_blank', 'width=560,height=760');
  if (!win) {
    // eslint-disable-next-line no-alert
    alert('無法開啟列印視窗，請允許此網站的彈出視窗後再試。');
    return;
  }
  win.document.open();
  win.document.write(buildShelfLabelsHtml(slots));
  win.document.close();
}
